import React, { useState } from "react";
import KuikaSelectbox, {
  KuikaSelectboxOption,
  KuikaSelectboxOptionGroup
} from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";
import { VariantGrid } from "../_layout/variant-grid";

const COUNTRY_OPTIONS: KuikaSelectboxOption[] = [
  { value: "tr", label: "Türkiye" },
  { value: "de", label: "Germany" },
  { value: "us", label: "United States" },
  { value: "jp", label: "Japan" },
  { value: "br", label: "Brazil" }
];

// 30+ options: rc-select mounts its virtual list past ~a handful of rows, recycling DOM
// nodes as the list scrolls — the case the active-row highlight (`.rc-select-item-option-active`)
// has to survive (faz-1-L3-katman.md, "Tuzaklar": rows are reused, not one stable element).
const LONG_LIST: KuikaSelectboxOption[] = Array.from({ length: 40 }, (_, index) => ({
  value: `option-${index}`,
  label: `Option ${index + 1}`
}));

const GROUPED: KuikaSelectboxOptionGroup[] = [
  {
    label: "Europe",
    options: [
      { value: "tr", label: "Türkiye" },
      { value: "de", label: "Germany" },
      { value: "uk", label: "United Kingdom" }
    ]
  },
  {
    label: "Americas",
    options: [
      { value: "us", label: "United States" },
      { value: "br", label: "Brazil" },
      { value: "ca", label: "Canada" }
    ]
  }
];

type SelectValue = string | number | (string | number)[] | null | undefined;

interface IStatefulProps {
  options?: KuikaSelectboxOption[];
  groups?: KuikaSelectboxOptionGroup[];
  size?: "sm" | "md" | "lg";
  mode?: "combobox" | "multiple" | "tags" | "treeView";
  disabled?: boolean;
  placeholder?: string;
  initial?: SelectValue;
}

const Stateful: React.FC<IStatefulProps> = ({ initial, ...rest }) => {
  const [value, setValue] = useState<SelectValue>(initial);
  return <KuikaSelectbox value={value} onChange={(v: SelectValue) => setValue(v)} {...rest} />;
};

export const SelectboxStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Selectbox"
      componentName="KuikaSelectbox"
      sourcePath="ui-kit/kuika-selectbox"
      description="Flexible select primitive built on rc-select. Single, multi, tags and combobox modes share one API."
    >
      <VariantGrid
        label="size"
        cols={["sm", "md", "lg"]}
        cellMinWidth={220}
        render={(size: string): React.ReactNode => (
          <Stateful options={COUNTRY_OPTIONS} size={size as "sm" | "md" | "lg"} placeholder="Pick a country" />
        )}
      />
      <SpecRow label="Mode" caption="combobox · multiple · tags" align="start">
        <div className="lab-stack" style={{ gap: 18, width: 280 }}>
          <span className="lab-caption">combobox</span>
          <Stateful options={COUNTRY_OPTIONS} mode="combobox" placeholder="Search countries" />
          <span className="lab-caption">multiple</span>
          <Stateful options={COUNTRY_OPTIONS} mode="multiple" initial={["tr", "de"]} placeholder="Pick many" />
          <span className="lab-caption">tags · free entry</span>
          <Stateful options={COUNTRY_OPTIONS} mode="tags" initial={["custom", "tr"]} placeholder="Type to add" />
        </div>
      </SpecRow>
      <SpecRow label="Grouped options" align="start">
        <div style={{ width: 280 }}>
          <Stateful groups={GROUPED} placeholder="Pick by region" />
        </div>
      </SpecRow>
      <SpecRow label="Disabled">
        <div style={{ width: 240 }}>
          <Stateful options={COUNTRY_OPTIONS} disabled placeholder="Locked" />
        </div>
      </SpecRow>
      <SpecRow
        label="Long list"
        caption="40 options — exercises the virtualized list; hover/↑↓ to see the active-row cursor"
        align="start"
      >
        <div style={{ width: 240 }}>
          <Stateful options={LONG_LIST} placeholder="Pick one of 40" />
        </div>
      </SpecRow>
    </ShowcaseFrame>
  );
};
